<template>
    <div class="vehicle-form">
        <loading
            :active.sync="form.busy || !$store.state.vehicle_makes.meta.fetched || !$store.state.vehicle_years.meta.fetched"
            :height="30"
            :is-full-page="false"
            :width="30"/>
        <div class="row basic-details">
            <div class="col-md-6 col-lg-4 p-0 p-sm-3">
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Enter Vin:</label>
                    <div class="col-sm-7 col-lg-8">
                        <div class="input-group">
                            <input :class="{ 'parsley-error' : form.errors.has('vin') }"
                                   class="form-control"
                                   title="vin"
                                   type="text"
                                   v-model="form.vin">
                            <div class="input-group-append">
                                <button @click="identifyVehicle"
                                        class="btn btn-primary waves-effect waves-light"
                                        type="button">
                                    <i class="mdi mdi-magnify"/>
                                    Search
                                </button>
                            </div>
                        </div>
                        <ul :class="{'filled' : form.errors.has('vin')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('vin')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Stock Id:</label>
                    <div class="col-sm-7 col-lg-8">
                        <input :class="{ 'parsley-error' : form.errors.has('stock_id') }"
                               @keypress="preventNegative"
                               class="form-control"
                               min="0"
                               title="stock_id"
                               type="text"
                               v-model="form.stock_id">
                        <ul :class="{'filled' : form.errors.has('stock_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('stock_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Make:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select
                            :class="{ 'parsley-error' : form.errors.has('vehicle_make_id') }"
                            @change="onSelectMake"
                            class="form-control"
                            title="make"
                            v-model="form.vehicle_make_id"
                        >
                            <option :value="null">Select make</option>
                            <option :key="make.id"
                                    :value="make.id"
                                    v-for="make in makes">{{ make.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('vehicle_make_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('vehicle_make_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Model:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select
                            :class="{ 'parsley-error' : form.errors.has('vehicle_model_id') }"
                            @change="onSelectModel"
                            class="form-control"
                            title="model"
                            v-model="form.vehicle_model_id"
                        >
                            <option :value="null">Select model</option>
                            <option :key="model.id"
                                    :value="model.id"
                                    v-for="model in models">{{ model.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('vehicle_model_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('vehicle_model_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Model Year:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select :class="{ 'parsley-error' : form.errors.has('vehicle_year_id') }" class="form-control"
                                title="model year"
                                v-model="form.vehicle_year_id">
                            <option :value="null">Select year</option>
                            <option :key="year.id"
                                    :value="year.id"
                                    v-for="year in years">{{ year.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('vehicle_year_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('vehicle_year_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Body:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select :class="{ 'parsley-error' : form.errors.has('body_id') }"
                                class="form-control"
                                title="body"
                                v-model="form.body_id">
                            <option :value="null">Select body</option>
                            <option :key="body.id"
                                    v-if="isMainBodyType(body)"
                                    :value="body.id"
                                    v-for="body in bodies">{{ body.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('body_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('body_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Trim:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select
                            :class="{ 'parsley-error' : form.errors.has('vehicle_trim_id') }"
                            class="form-control"
                            title="trim"
                            v-model="form.vehicle_trim_id"
                        >
                            <option :value="null">Select trim</option>
                            <option :key="trim.id"
                                    :value="trim.id"
                                    v-for="trim in trims">{{ trim.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('vehicle_trim_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('vehicle_trim_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Color:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select :class="{ 'parsley-error' : form.errors.has('color_id') }"
                                class="form-control"
                                title="color"
                                v-model="form.color_id">
                            <option :value="null">Select color</option>
                            <option :key="color.id"
                                    :value="color.id"
                                    v-for="color in colors">{{ color.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('color_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('color')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Trans:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select :class="{ 'parsley-error' : form.errors.has('transmission_id') }"
                                class="form-control"
                                title="transmission"
                                v-model="form.transmission_id">
                            <option :value="null">Select transmission</option>
                            <option :key="transmission.id"
                                    :value="transmission.id"
                                    v-for="transmission in transmissions">{{ transmission.name }}
                            </option>
                        </select>
                        <ul :class="{'filled' : form.errors.has('transmission_id')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('transmission_id')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-5 col-lg-4 col-form-label">Mileage:</label>
                    <div class="col-sm-7 col-lg-8">
                        <input :class="{ 'parsley-error' : form.errors.has('mileage') }"
                               @keypress="preventNegative"
                               class="form-control"
                               min="0"
                               title="mileage"
                               type="number"
                               v-model="form.mileage">
                        <ul :class="{'filled' : form.errors.has('mileage')}"
                            class="parsley-errors-list">
                            <li class="parsley-required">{{
                                    form.errors.get('mileage')
                                }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="col-md-6 col-lg-8 p-0 p-sm-3">
                <div class="row">
                    <div class="col-12 col-xl-6 p-2">
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Days on Floor:</label>
                            <div class="col-sm-7 col-lg-8">
                                <input :class="{ 'parsley-error' : form.errors.has('days_on_floor') }"
                                       @keypress="preventNegative"
                                       class="form-control"
                                       min="0"
                                       title="days on floor"
                                       type="number"
                                       v-model="form.days_on_floor">
                                <ul :class="{'filled' : form.errors.has('days_on_floor')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">{{
                                            form.errors.get('days_on_floor')
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Fuel Type:</label>
                            <div class="col-sm-7 col-lg-8">
                                <select :class="{ 'parsley-error' : form.errors.has('fuel_type_id') }"
                                        class="form-control"
                                        title="fuel type"
                                        v-model="form.fuel_type_id">
                                    <option :value="null">Select fuel type</option>
                                    <option :key="fuel_type.id"
                                            :value="fuel_type.id"
                                            v-for="fuel_type in fuel_types">{{ fuel_type.name }}
                                    </option>
                                </select>
                                <ul :class="{'filled' : form.errors.has('fuel_type_id')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">{{
                                            form.errors.get('fuel_type_id')
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Number of Doors:</label>
                            <div class="col-sm-7 col-lg-8">
                                <select :class="{ 'parsley-error' : form.errors.has('number_of_doors_id') }"
                                        class="form-control"
                                        title="number of doors"
                                        v-model="form.number_of_doors_id">
                                    <option :value="null">Select number of door</option>
                                    <option :key="number_of_door.id"
                                            :value="number_of_door.id"
                                            v-for="number_of_door in number_of_doors">{{ number_of_door.name }}
                                    </option>
                                </select>
                                <ul :class="{'filled' : form.errors.has('number_of_doors_id')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">{{
                                            form.errors.get('number_of_doors_id')
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Drive:</label>
                            <div class="col-sm-7 col-lg-8">
                                <select :class="{ 'parsley-error' : form.errors.has('drive_id') }"
                                        class="form-control"
                                        title="drive"
                                        v-model="form.drive_id">
                                    <option :value="null">Select drive</option>
                                    <option :key="drive.id"
                                            :value="drive.id"
                                            v-for="drive in drives">{{ drive.name }}
                                    </option>
                                </select>
                                <ul :class="{'filled' : form.errors.has('drive_id')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">{{
                                            form.errors.get('drive_id')
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Price:</label>
                            <div class="col-sm-7 col-lg-8">
                                <input :class="{ 'parsley-error' : form.errors.has('price') }"
                                       @keypress="preventNegative"
                                       class="form-control"
                                       min="0"
                                       title="price"
                                       type="number"
                                       v-model="form.price">
                                <ul :class="{'filled' : form.errors.has('price')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">{{
                                            form.errors.get('price')
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Condition:</label>
                            <div class="col-sm-7 col-lg-8">
                                <select :class="{ 'parsley-error' : form.errors.has('condition_id') }"
                                        class="form-control"
                                        title="condition"
                                        v-model="form.condition_id">
                                    <option :value="null">Select Condition</option>
                                    <option :key="condition.id"
                                            :value="condition.id"
                                            v-for="condition in conditions">{{ condition.name }}
                                    </option>
                                </select>
                                <ul :class="{'filled' : form.errors.has('condition_id')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">
                                        {{ form.errors.get('condition_id') }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-5 col-lg-4 col-form-label">Assignee:</label>
                            <div class="col-sm-7 col-lg-8">
                                <select :class="{ 'parsley-error' : form.errors.has('assignee_id') }"
                                        class="form-control"
                                        title="assignee"
                                        v-model="form.assignee_id">
                                    <option :value="null" disabled>Select Assignee</option>
                                    <option :key="user.id"
                                            :value="user.id"
                                            v-for="user in users">
                                        {{ user.name }} {{ user.id === authUser.id ? '(Assign to me)' : '' }}
                                    </option>
                                </select>
                                <ul :class="{'filled' : form.errors.has('assignee_id')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">
                                        {{ form.errors.get('assignee_id') }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-6 col-lg-6 col-form-label">Add Carfax Report PDF</label>
                            <div class="col-sm-6 col-lg-6">
                                <input @change="onCarfaxReportFileChange"
                                       ref="carfaxFileInput"
                                       type='file'
                                       v-show="false"/>
                                <img @click="addCarfaxReport"
                                     alt=""
                                     class="carfax-button-icon"
                                     src="/images/carfax.jfif"
                                     width="100%">
                            </div>
                            <div class="mt-2">
                                <span class="badge badge-info m-1" v-for="report in form.carfax_reports">
                                    {{ report.name }}
                                    <i @click="removeCarfaxReport(report)" class="mdi mdi-close"/>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-xl-6">
                        <image-swiper
                            :can-remove="!form.busy"
                            :can-set-front-image="!form.busy"
                            :images="imageUrls"
                            :default-image="defaultImageUrl"
                            @frontItem="onFrontItem"
                            @removeItem="onRemoveItem"
                            ref="imageSwiper">
                            <div>
                                <input @change="onCarImageFileChange" accept="image/*"
                                       multiple
                                       ref="carImageFileInput"
                                       type='file'
                                       v-show="false"/>
                                <div class="d-flex justify-content-between">
                                    <button @click="addImage"
                                            class="btn btn-primary waves-effect waves-light"
                                            type="button">
                                        <i class="mdi mdi-plus"/>
                                        Add Photos
                                    </button>
                                    <p class="my-auto d-flex align-items-center">
                                        <span class="mr-1">
                                            <i class="mdi mdi-information-outline mdi-18px text-info ml-1"
                                               ref="addImageInfoPopover"
                                            />
                                        </span>
                                        {{ this.all_images.length }} photos
                                    </p>
                                </div>
                                <ul :class="{'filled' : form.errors.has('images')}"
                                    class="parsley-errors-list">
                                    <li class="parsley-required">
                                        {{ form.errors.get('images') }}
                                    </li>
                                </ul>
                            </div>
                        </image-swiper>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <div class="row mt-3 mt-sm-0">
                <div class="col-sm-12 px-2 px-sm-3">
                    <div class="form-group">
                        <div v-if="form.additional_information.length">
                            <div v-if="vehicle && vehicle.provider === 'home net'">
                                <div class="d-flex justify-content-between">
                                    <label class="col-form-label">Options:</label>
                                    <span class="toggle-options d-flex align-items-center font-weight-bold" @click="showOptions = !showOptions">
                                        {{showOptions ? 'Show Less' : 'Show More'}} <i :class="showOptions ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"/>
                                    </span>
                                </div>
                                <div  v-show="showOptions">
                                    <div class="row">
                                        <div
                                            v-for="item in form.additional_information"
                                            class="custom-control custom-checkbox col-md-3 col-12 px-2"
                                        >
                                            <span>
                                                <i class="mdi mdi-checkbox-marked-circle info-check-icon"/>
                                                {{ item.name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label class="col-form-label">Additional Information:</label>
                                <div>
                                    <div class="row">
                                        <div v-for="item in form.additional_information"
                                             v-if="item.meta.availability === InfoAvailabilities.standard"
                                             class="custom-control custom-checkbox col-md-3 col-12 px-2">
                                    <span>
                                        <i class="mdi mdi-checkbox-marked-circle info-check-icon"/>
                                        {{ item.name }}
                                    </span>
                                        </div>
                                    </div>
                                    <div class="row external-details mt-3">
                                        <div v-for="(external_vehicle_detail, key) in ExternalVehicleDetails"
                                             class="custom-control custom-checkbox col-md-3 col-12 px-2">
                                            <div>
                                        <span class="bold">
                                            {{ external_vehicle_detail.label }}:&nbsp;
                                        </span>
                                                {{ form.detail[key] }}
                                            </div>
                                        </div>
                                        <div class="custom-control custom-checkbox col-md-3 col-12 px-2">
                                            <div>
                                        <span class="bold">
                                            Maximum Towing:&nbsp;
                                            {{
                                                getExternalVehicleMaxTowing(form.additional_information)
                                                    ? getExternalVehicleMaxTowing(form.additional_information)
                                                    : 'N/A'
                                            }}
                                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else>No data...</p>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12 px-2 px-sm-3">
                    <div class="form-group">
                        <label class="col-form-label">Additional Features / Accessories: </label>
                        <div>
                            <textarea :class="{ 'parsley-error' : form.errors.has('additional_features') }"
                                      :rows="4"
                                      class="form-control"
                                      title="additional features"
                                      type="text"
                                      v-model="form.additional_features"/>
                            <ul :class="{'filled' : form.errors.has('additional_features')}"
                                class="parsley-errors-list">
                                <li class="parsley-required">{{
                                        form.errors.get('additional_features')
                                    }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-6 px-2 px-sm-3">
                    <div class="form-group">
                        <label class="col-form-label">Deal Conditions:</label>
                        <div>
                            <textarea :class="{ 'parsley-error' : form.errors.has('deal_conditions') }"
                                      :rows="4"
                                      class="form-control"
                                      title="deal conditions"
                                      type="text"
                                      v-model="form.deal_conditions"/>
                            <ul :class="{'filled' : form.errors.has('deal_conditions')}"
                                class="parsley-errors-list">
                                <li class="parsley-required">{{
                                        form.errors.get('deal_conditions')
                                    }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-sm-6 px-2 px-sm-3">
                    <div class="form-group">
                        <label class="col-form-label">Physical Transfer Info:</label>
                        <div>
                            <textarea :class="{ 'parsley-error' : form.errors.has('physical_transfer_info') }"
                                      :rows="4"
                                      class="form-control"
                                      title="physical transfer info"
                                      type="text"
                                      v-model="form.physical_transfer_info"/>
                            <ul :class="{'filled' : form.errors.has('physical_transfer_info')}"
                                class="parsley-errors-list">
                                <li class="parsley-required">{{
                                        form.errors.get('physical_transfer_info')
                                    }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div class="float-md-left mb-2"
                 v-if="vehicle && hasRole([Roles.dealer_principle, Roles.sales_manager]) && vehicle.company_id === auth.user.companies[0].id">
                <div
                    class="d-flex align-items-center"
                >
                    <button
                        @click="deleteVehicle()"
                        class="btn btn-danger waves-effect waves-light"
                        type="button"
                    >
                        <i class="mdi mdi-trash-can"/>
                        Delete
                    </button>
                    <div class="d-flex ml-3">
                        <p class="m-0 pr-2 listings-only-title">Hide Listing</p>
                        <input
                            @change="hideVehicle()"
                            checked
                            id="switch1"
                            switch="none"
                            type="checkbox"
                            v-model="hide_listing"
                        />
                        <label
                            class="m-0"
                            data-off-label="No"
                            data-on-label="Yes"
                            for="switch1"
                        />
                    </div>
                    <div class="d-flex ml-3">
                        <p class="m-0 pr-2 listings-only-title">Sold Externally</p>
                        <input
                            @change="onChangeSoldExternally()"
                            checked
                            id="switch2"
                            switch="none"
                            type="checkbox"
                            v-model="is_sold_externally"
                        />
                        <label
                            class="m-0"
                            data-off-label="No"
                            data-on-label="Yes"
                            for="switch2"
                        />
                    </div>
                </div>
            </div>
            <div class="float-md-right">
                <button @click="cancel" class="btn btn-secondary waves-effect waves-light"
                        type="button">
                    <i class="mdi mdi-close"/>
                    Cancel
                </button>
                <button :disabled="form.busy"
                        @click="submit"
                        class="btn btn-primary waves-effect waves-light" type="button">
                    <i class="mdi mdi-content-save"/>
                    {{ vehicle ? 'Save Changes' : 'Submit' }}
                </button>
            </div>
            <div class="clearfix"></div>
        </div>
    </div>
</template>

<script>
import {get} from 'lodash';
import VueScrollTo from "vue-scrollto";

import ImageSwiper from "@/components/custom/ImageSwiper";

import InfoAvailabilities from "@/constants/info-availabilities";
import Getters from "@/constants/store/getters";
import Routes from "@/constants/routes";
import Vehicle from "@/models/vehicle";
import ArrayUtils from "@/utils/array";
import ExternalVehicleDetails from "@/constants/vehicle/external_vehicle_details";
import TaxonomyOption from "@/models/taxonomy-option";
import vehicle_mixin from "@/mixins/vehicle"
import User from "@/models/user";
import VehicleUtils from "@/utils/vehicle";
import Roles from "@/constants/roles";
import Actions from "@/constants/store/actions";

const IMAGE_TYPES = {
    file: 'file',
    url: 'url'
};

export default {
    mixins: [
        vehicle_mixin
    ],
    props: {
        vehicle: {
            type: Object,
        }
    },
    components: {
        ImageSwiper
    },
    data() {
        return {
            InfoAvailabilities,
            IMAGE_TYPES,
            ExternalVehicleDetails,
            Roles,
            form: new Form({
                id: null,
                vin: null,
                stock_id: null,
                vehicle_make_id: null,
                vehicle_model_id: null,
                vehicle_year_id: null,
                body_id: null,
                vehicle_trim_id: null,
                color_id: null,
                transmission_id: null,
                mileage: null,
                days_on_floor: null,
                fuel_type_id: null,
                number_of_doors_id: null,
                drive_id: null,
                price: null,
                condition_id: null,
                additional_information: [],
                additional_features: null,
                deal_conditions: null,
                physical_transfer_info: null,
                images: [],
                image_urls: [],
                carfax_reports: [],
                assignee_id: null,
                deleted_images: [],
                detail: {
                    number_of_doors: null,
                    fuel_type: null,
                    fuel_capacity: null,
                    city_mileage: null,
                    highway_mileage: null,
                    engine: null,
                    engine_size: null,
                    engine_cylinders: null,
                    transmission: null,
                    transmission_full_name: null,
                    transmission_speeds: null,
                    drive: null,
                    anti_brake_system: null,
                    steering_type: null
                },
                front_image: {
                    id: null,
                    type: null,
                    index: null
                }
            }, {
                vin: [
                    'string',
                    'max:255',
                ],
                stock_id: [
                    'string',
                    'max:255',
                    'stock_id' + (this.vehicle ? ':' + this.vehicle.id : '')
                ],
                vehicle_make_id: 'required|integer',
                vehicle_model_id: 'required|integer',
                vehicle_year_id: 'required|integer',
                body_id: 'integer',
                vehicle_trim_id: 'integer',
                color_id: 'integer',
                transmission_id: 'integer',
                mileage: 'numeric',
                days_on_floor: 'integer',
                fuel_type_id: 'integer',
                number_of_doors_id: 'integer',
                drive_id: 'integer',
                price: 'required|numeric',
                condition_id: 'integer',
                additional_information: 'array',
                additional_features: 'string',
                deal_conditions: 'string',
                physical_transfer_info: 'string',
                images: 'array' + (this.vehicle ? '' : '|required_without:image_urls'),
                image_urls: 'array' + (this.vehicle ? '' : '|required_without:images'),
                assignee_id: 'integer',
                deleted_images: 'array',
                detail: {
                    number_of_doors: 'integer',
                    fuel_type: 'string|max:255',
                    fuel_capacity: 'string|max:255',
                    city_mileage: 'string|max:255',
                    highway_mileage: 'string|max:255',
                    engine: 'string|max:255',
                    engine_size: 'string|max:255',
                    engine_cylinders: 'integer',
                    transmission: 'string|max:255',
                    transmission_full_name: 'string|max:255',
                    transmission_speeds: 'string|max:255',
                    drive: 'string|max:255',
                    anti_brake_system: 'string|max:255',
                    steering_type: 'string|max:255'
                },
            }, {
                "required_without.images": "Must have at least 1 photo",
                "required_without.image_urls": "Must have at least 1 photo"
            }),
            is_form_dirty: false,
            all_images: [],
            users: [],
            image_urls: [],
            hide_listing: true,
            is_sold_externally: false,
            selected_make: null,
            selected_model: null,
            selected_trim: null,
            showOptions: false,
        }
    },
    async created() {
        if (this.vehicle) {
            _.extend(
                this.form,
                _.pick(
                    _.cloneDeep(this.vehicle),
                    [
                        'id',
                        'vin',
                        'stock_id',
                        'vehicle_make_id',
                        'vehicle_model_id',
                        'vehicle_year_id',
                        'body_id',
                        'vehicle_trim_id',
                        'color_id',
                        'transmission_id',
                        'mileage',
                        'days_on_floor',
                        'fuel_type_id',
                        'number_of_doors_id',
                        'drive_id',
                        'price',
                        'condition_id',
                        'additional_features',
                        'deal_conditions',
                        'physical_transfer_info',
                        'assignee_id',
                        'detail',
                        'additional_information'
                    ]
                )
            );
            this.hide_listing = !!this.vehicle.hid_at;
            this.is_sold_externally = this.vehicle.is_sold_externally;
            this.initVehicleImages()
        }
        await this.initUsers();
        this.is_form_dirty = false;
    },
    async mounted() {
        $(this.$refs.addImageInfoPopover).popover({
            content: "Upload at least 1 exterior and 1 interior photo.",
            trigger: 'hover',
            placement: 'top'
        });
        await this.initVehicleYears();
        await this.initVehicleMakes();
        if (this.vehicle) {
            this.selected_make = _.find(this.makes, {id: this.vehicle.vehicle_make_id});
            this.selected_model = _.find(this.models, {id: this.vehicle.vehicle_model_id});
            this.selected_trim = _.find(this.trims, {id: this.vehicle.vehicle_trim_id});
        }
    },
    computed: {
        makes() {
            return this.$store.state.vehicle_makes.data;
        },
        models() {
            if (!this.selected_make) {
                return [];
            }
            return this.selected_make.vehicle_models;
        },
        trims() {
            if (!this.selected_model) {
                return [];
            }
            return this.selected_model.vehicle_trims;
        },
        bodies() {
            return this.$store.getters[Getters.get_bodies]
        },
        colors() {
            return this.$store.getters[Getters.get_colors]
        },
        transmissions() {
            return this.$store.getters[Getters.get_transmissions]
        },
        fuel_types() {
            return this.$store.getters[Getters.get_fuel_types]
        },
        number_of_doors() {
            return this.$store.getters[Getters.get_number_of_doors]
        },
        drives() {
            return this.$store.getters[Getters.get_drives]
        },
        conditions() {
            return this.$store.getters[Getters.get_conditions]
        },
        years() {
            return this.$store.state.vehicle_years.data;
        },
        imageUrls() {
            return _.map(this.all_images, image => {
                if (image instanceof File) {
                    return URL.createObjectURL(image);
                }
                return image;
            })
        },
        defaultImageUrl() {
            let url;
            switch (this.form.front_image.type) {
                case IMAGE_TYPES.file:
                    let index = -1;
                    _.forEach(this.imageUrls, (image_url, i) => {
                        if (image_url.substr(0, 5) === 'blob:') {
                            index++;
                        }
                        if (index === this.form.front_image.index) {
                            url = image_url;
                        }
                    });
                    break;
                case IMAGE_TYPES.url:
                    url = this.form.image_urls[this.form.front_image.index];
                    break;
                default:
                    if (this.form.front_image.id) {
                        let image_media = _.find(this.vehicle.images, {id: this.form.front_image.id});
                        if (image_media) {
                            url = image_media.url;
                        }
                    }
                    break;
            }
            return url;
        },
        authUser() {
            return this.$store.state.user
        }
    },
    methods: {
        async initVehicleYears() {
            if (this.$store.state.vehicle_years.meta.fetched) {
                return;
            }
            await this.$store.dispatch(Actions.fetch_vehicle_years);
        },
        async initVehicleMakes() {
            if (this.$store.state.vehicle_makes.meta.fetched) {
                return;
            }
            await this.$store.dispatch(Actions.fetch_vehicle_makes);
        },
        initVehicleImages() {
            this.form.front_image.id = null;
            this.form.front_image.type = null;
            this.form.front_image.index = null;
            if (this.vehicle) {
                VehicleUtils.arrangeVehicleImages(this.vehicle.images);
                this.all_images = _.map(this.vehicle.images, (image) => {
                    return image.url;
                });
                let default_image = _.find(this.vehicle.images, image => {
                    return image.custom_properties.is_front
                });
                if (default_image) {
                    this.form.front_image.id = default_image.id;
                }
            }
        },
        fetchUsers() {
            return User.params({
                no_pagination: 1
            })
                .include('roles')
                .get();
        },
        initUsers() {
            return this.fetchUsers()
                .then((response) => {
                    this.users = response;
                    if (!this.form.assignee_id) {
                        _.forEach(this.users, (user) => {
                            let has_dealer_role = _.find(user.roles, {name: Roles.dealer_principle});
                            if (has_dealer_role) {
                                this.form.assignee_id = user.id;
                                return false;
                            }
                        })
                    }
                });
        },
        addCarfaxReport() {
            this.$refs.carfaxFileInput.click();
        },
        onCarfaxReportFileChange(e) {
            if (!e.target.files.length) {
                return;
            }
            this.form.carfax_reports.push(e.target.files[0]);
            this.$refs.carfaxFileInput.value = '';
        },
        removeCarfaxReport(report_file) {
            ArrayUtils.delete(this.form.carfax_reports, report_file);
        },
        addImage() {
            this.$refs.carImageFileInput.click();
        },
        onCarImageFileChange(e) {
            if (!e.target.files.length) {
                return;
            }
            e.target.files.forEach((file) => {
                this.form.images.push(file);
                this.all_images.push(file);
                let path = URL.createObjectURL(file);
                this.image_urls.push(path);
            });
            this.$refs.carImageFileInput.value = '';
            this.$refs.imageSwiper.setSwiperHeight();
        },
        preventNegative(e) {
            let inputKeyCode = e.keyCode ? e.keyCode : e.which;
            if (inputKeyCode != null) {
                if (inputKeyCode === 45) e.preventDefault();
            }
        },
        identifyVehicle() {
            if (!this.form.vin) {
                return;
            }
            axios.get('/web-api/identify-vehicle?vin=' + this.form.vin)
                .then((response) => {
                    let vehicle = response.data;

                    this.form.additional_information = _.map(_.filter(vehicle.additional_information, (item) => {
                        if (item.name === 'Maximum Towing') {
                            return true;
                        }
                        return item.availability === InfoAvailabilities.standard;
                    }), (item) => {
                        return {
                            name: item.name,
                            meta: item
                        }
                    });

                    let make = _.find(this.makes, {name: vehicle.make});
                    this.form.vehicle_make_id = _.get(make, 'id');
                    this.selected_make = make;

                    let model = _.find(this.models, {name: vehicle.model});
                    this.form.vehicle_model_id = _.get(model, 'id');
                    this.selected_model = model;

                    this.form.vehicle_year_id = _.get(_.find(this.years, {name: vehicle.model_year + ''}), 'id');

                    this.form.body_id = _.get(_.find(this.bodies, (item) => {
                        let matched_on_abbreviation = false;
                        if (item.name.isUpperCase()) {
                            matched_on_abbreviation = item.name === vehicle.body.getFirstChars().toUpperCase();
                        }
                        if (matched_on_abbreviation) {
                            return true;
                        }
                        return item.name === vehicle.body;
                    }), 'id', null);

                    this.form.vehicle_trim_id = _.get(_.find(this.trims, {name: vehicle.trim}), 'id', null);

                    if (vehicle.transmission) {
                        let transmission_id = _.get(_.find(this.transmissions, {name: vehicle.transmission}), 'id');
                        this.form.transmission_id = transmission_id ? transmission_id : TaxonomyOption.transmission_other;
                    } else {
                        this.form.transmission_id = null;
                    }

                    if (vehicle.fuel_type) {
                        let fuel_type_id = _.get(_.find(this.fuel_types, {name: vehicle.fuel_type}), 'id');
                        this.form.fuel_type_id = fuel_type_id ? fuel_type_id : TaxonomyOption.fuel_type_unknown;
                    } else {
                        this.form.fuel_type_id = null;
                    }

                    if (vehicle.number_of_doors) {
                        let number_of_doors_id = _.get(_.find(this.number_of_doors, (item) => {
                            return item.meta.value === vehicle.number_of_doors
                        }), 'id');
                        if (!number_of_doors_id && vehicle.number_of_doors > 4) {
                            return this.form.number_of_doors_id = TaxonomyOption.number_of_doors_4plus;
                        }
                        this.form.number_of_doors_id = number_of_doors_id ? number_of_doors_id : TaxonomyOption.number_of_doors_unknown;
                    } else {
                        this.form.number_of_doors_id = null;
                    }

                    if (vehicle.drive) {
                        let drive_id = _.get(_.find(this.drives, {name: _.replace(vehicle.drive, '-', ' ')}), 'id');
                        this.form.drive_id = drive_id ? drive_id : TaxonomyOption.drive_unknown;
                    } else {
                        this.form.drive_id = null;
                    }
                    this.form.detail = _.pick(vehicle, [
                        'number_of_doors',
                        'fuel_type',
                        'fuel_capacity',
                        'city_mileage',
                        'highway_mileage',
                        'engine',
                        'engine_size',
                        'engine_cylinders',
                        'transmission',
                        'transmission_full_name',
                        'transmission_speeds',
                        'drive',
                        'anti_brake_system',
                        'steering_type'
                    ]);
                    this.form.detail.engine_size = `${this.form.detail.engine_size}`;
                    this.form.images = [];
                    this.form.image_urls = vehicle.photos;
                    this.all_images = [];
                    vehicle.photos.forEach((photo_url) => {
                        if (!this.all_images.includes(photo_url)) {
                            this.all_images.push(photo_url);
                        }
                    });
                    this.$nextTick(() => {
                        this.$refs.imageSwiper.setSwiperHeight();
                    });
                })
        },
        onRemoveItem(item, index) {
            if (this.vehicle) {
                let media = _.find(this.vehicle.images, {url: item});
                if (media) {
                    this.form.deleted_images.push(media.id);
                }
            }

            let deleting_image = this.all_images[index];
            let index_in_image_urls = _.indexOf(this.form.image_urls, deleting_image);
            if (index_in_image_urls !== -1) {
                ArrayUtils.delete(this.form.image_urls, deleting_image);
            }
            let index_in_images = _.indexOf(this.form.images, deleting_image);
            if (index_in_images !== -1) {
                ArrayUtils.delete(this.form.images, deleting_image);
            }
            ArrayUtils.delete(this.all_images, deleting_image);
        },
        onFrontItem(image_url, index) {
            let type = null;
            let images_index = null;
            let index_in_image_urls = _.indexOf(this.form.image_urls, this.all_images[index]);
            if (index_in_image_urls !== -1) {
                type = IMAGE_TYPES.url;
                images_index = index_in_image_urls
            }
            let index_in_images = _.indexOf(this.form.images, this.all_images[index]);
            if (index_in_images !== -1) {
                type = IMAGE_TYPES.file;
                images_index = index_in_images
            }
            if (type) {
                this.form.front_image.type = type;
                this.form.front_image.index = images_index;
                this.form.front_image.id = null;
            } else {
                this.form.front_image.id = this.vehicle.images[index].id;
                this.form.front_image.type = null;
                this.form.front_image.index = null;
            }
        },
        submit() {
            let clone_form = _.cloneDeep(this.form);
            if (!clone_form.images.length) {
                clone_form = _.omit(clone_form, 'images')
            }
            if (!clone_form.image_urls.length) {
                clone_form = _.omit(clone_form, 'image_urls')
            }
            clone_form.validate()
                .then(() => {
                    this.$confirm(this.vehicle ? 'Confirm save changes?' : 'Are you sure you want to submit this listing?', 'Confirm', {
                        confirmButtonText: 'OK',
                        cancelButtonText: 'Cancel',
                        type: 'info'
                    }).then(() => {
                        this.form.busy = true;
                        let vehicle = new Vehicle(this.form);
                        let then = (response) => {
                            _.extend(this.vehicle, response.data);
                            this.$refs.carImageFileInput.value = '';
                            this.$refs.carfaxFileInput.value = '';
                            this.all_images = [];
                            this.form.images = [];
                            this.form.image_urls = [];
                            this.image_urls = [];
                            this.form.deleted_images = [];
                            this.initVehicleImages();
                            this.$nextTick(() => {
                                this.is_form_dirty = false;
                            });
                            this.$emit('success', response.data);
                        };
                        let params = 'include=images';
                        if (this.vehicle) {
                            vehicle.updateData(params)
                                .then(response => {
                                    this.$notify.success({
                                        title: 'Success',
                                        message: 'Changes have been saved.',
                                    });
                                    return response
                                })
                                .then(then)
                        } else {
                            vehicle.saveData(params)
                                .then(response => {
                                    this.form.clearFields();
                                    this.$notify.success({
                                        title: 'Success',
                                        message: 'New listing has been created.',
                                    });
                                    VueScrollTo.scrollTo('#page_title', {offset: -120});
                                    return response;
                                })
                                .then(then)
                        }
                    })
                        .catch(() => {
                        })
                })
                .catch((errors) => {
                    this.form.setErrors(errors);
                })
        },
        cancel() {
            if (get(this.$route, 'query.back')) {
                this.$router.push(this.$route.query.back);
                return;
            }
            this.$router.push({
                name: Routes.dashboard
            });
        },
        deleteVehicle() {
            this.$confirm('Are you sure you want to permanently delete this listing?', 'Confirm', {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                type: 'error'
            })
                .then(() => {
                    axios.delete(`/web-api/vehicle/${this.vehicle.id}`)
                        .then(() => {
                            this.$notify.success({
                                title: 'Success',
                                message: 'Successfully deleted the listing.',
                            });
                            this.$router.replace({
                                name: Routes.vehicle
                            })
                        })
                })
                .catch(() => {
                    //
                })
        },
        hideVehicle() {
            this.$confirm(`Are you sure you want to ${this.hide_listing ? 'hide' : 'un-hide'} this listing?`, 'Confirm', {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                type: 'warning'
            })
                .then(() => {
                    axios.post(`/web-api/vehicle/${this.vehicle.id}/hide`, {
                        hide: this.hide_listing
                    })
                        .then(() => {
                            this.$notify.success({
                                title: 'Success',
                                message: `Successfully ${this.hide_listing ? 'hide' : 'un-hid'} the listing.`,
                            });
                        })
                })
                .catch(() => {
                    this.hide_listing = !this.hide_listing;
                })
        },
        onChangeSoldExternally() {
            this.$confirm(
                `Are you sure you want to ${this.is_sold_externally ? 'mark' : 'un-mark'} this listing as sold externally?`,
                'Confirm',
                {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }
            )
                .then(() => {
                    axios.put(`/web-api/vehicle/${this.vehicle.id}`, {
                        is_sold_externally: this.is_sold_externally
                    })
                        .then(() => {
                            this.$notify.success({
                                title: 'Success',
                                message: `Listing has been ${this.is_sold_externally ? 'marked' : 'un-marked'} as sold externally.`,
                            });
                        })
                })
                .catch(() => {
                    this.is_sold_externally = !this.is_sold_externally;
                })
        },
        onSelectMake(e) {
            this.selected_make = _.find(this.makes, {id: parseInt(e.target.value)});
            this.form.vehicle_model_id = null;
            this.selected_model = null;
            this.form.vehicle_trim_id = null;
            this.selected_trim = null;
        },
        onSelectModel(e) {
            this.selected_model = _.find(this.models, {id: parseInt(e.target.value)});
            this.form.vehicle_trim_id = null;
            this.selected_trim = null;
        }
    },
    watch: {
        form: {
            handler: function () {
                if (!this.is_form_dirty) {
                    this.is_form_dirty = true;
                }
            },
            deep: true
        }
    }
}
</script>

<style scoped>
.toggle-options{
    color: #59bdd3;
    cursor: pointer;
}
</style>
